import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Search, FileText, Copy, Sparkles, ExternalLink, ChevronDown, ChevronRight, Wand2, TrendingUp, BarChart3, AlertTriangle, Columns3, Zap, History } from "lucide-react";
import { toast } from "sonner";
import RewriteHistory from "@/components/RewriteHistory";

const MODES = [
  { k: "tone", label: "Punchier tone", desc: "Active voice, scannable, exciting." },
  { k: "inclusivity", label: "More inclusive", desc: "Remove biased language + soften must-haves." },
  { k: "seniority", label: "Adjust seniority", desc: "Reshape responsibilities for a target level." },
];

const COMPARE_LABELS = {
  tone: { label: "Punchier tone", color: "bg-pink" },
  inclusivity: { label: "More inclusive", color: "bg-teal" },
  seniority: { label: "Adjust seniority", color: "bg-amber-500" },
};

export default function JobDescriptionsPage() {
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [reuse, setReuse] = useState(null);
  const [reuseTitle, setReuseTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const [rewrite, setRewrite] = useState(null);
  const [rewriteMode, setRewriteMode] = useState("tone");
  const [rewriteSeniority, setRewriteSeniority] = useState("Senior");
  const [rewriteBusy, setRewriteBusy] = useState(false);
  const [rewriteResult, setRewriteResult] = useState("");
  const [applying, setApplying] = useState(false);
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareResults, setCompareResults] = useState(null);
  const [compareChoice, setCompareChoice] = useState(null);
  const [autoFix, setAutoFix] = useState(null); // {jobId, title}
  const [autoFixResult, setAutoFixResult] = useState(null);
  const [autoFixBusy, setAutoFixBusy] = useState(false);
  const [autoFixApplying, setAutoFixApplying] = useState(false);
  const [historyJobId, setHistoryJobId] = useState(null);

  const load = () => api.get("/recruit/job-descriptions").then(r => setItems(r.data)).catch(() => {});
  const loadAnalytics = () => api.get("/recruit/jd-analytics").then(r => setAnalytics(r.data)).catch(() => {});
  useEffect(() => { load(); loadAnalytics(); }, []);

  const analyticsById = useMemo(() => Object.fromEntries(analytics.map(a => [a.job_id, a])), [analytics]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter(x => `${x.title} ${x.department} ${x.description}`.toLowerCase().includes(t));
  }, [items, q]);

  const topPerformers = useMemo(() => analytics.filter(a => a.avg_ai_score != null).slice(0, 3), [analytics]);
  const flagged = useMemo(() => analytics.filter(a => a.alert), [analytics]);

  const copyText = (text) => {
    navigator.clipboard.writeText(text || "").then(() => toast.success("Description copied"));
  };

  const reuseAsNewJob = async () => {
    if (!reuse || !reuseTitle) return;
    setSaving(true);
    try {
      await api.post("/recruit/jobs", {
        title: reuseTitle,
        department: reuse.department || "Engineering",
        location: reuse.location || "Remote",
        description: reuse.description || "",
        requirements: reuse.requirements || [],
      });
      toast.success("New job created from description");
      setReuse(null); setReuseTitle("");
      load(); loadAnalytics();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Create failed"); }
    finally { setSaving(false); }
  };

  const openRewrite = (job) => {
    setRewrite({ job });
    setRewriteMode("tone");
    setRewriteSeniority("Senior");
    setRewriteResult("");
    setCompareResults(null);
    setCompareChoice(null);
  };

  const runRewrite = async () => {
    if (!rewrite) return;
    setRewriteBusy(true);
    try {
      const { data } = await api.post("/ai/rewrite-jd", {
        description: rewrite.job.description,
        mode: rewriteMode,
        seniority: rewriteMode === "seniority" ? rewriteSeniority : null,
      });
      setRewriteResult(data.description);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Rewrite failed"); }
    finally { setRewriteBusy(false); }
  };

  const runCompare = async () => {
    if (!rewrite) return;
    setCompareBusy(true);
    setCompareResults(null);
    setCompareChoice(null);
    try {
      const { data } = await api.post("/ai/rewrite-jd-compare", {
        description: rewrite.job.description,
        seniority: rewriteSeniority,
      });
      setCompareResults(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Compare failed"); }
    finally { setCompareBusy(false); }
  };

  const applyRewrite = async () => {
    if (!rewrite) return;
    const chosen = compareResults && compareChoice ? compareResults[compareChoice] : rewriteResult;
    if (!chosen) return;
    setApplying(true);
    try {
      const meta = compareResults && compareChoice
        ? { source: "compare", mode: compareChoice, seniority: compareChoice === "seniority" ? rewriteSeniority : null }
        : { source: "rewrite", mode: rewriteMode, seniority: rewriteMode === "seniority" ? rewriteSeniority : null };
      await api.patch(`/recruit/jobs/${rewrite.job.job_id}`, { description: chosen, _rewrite_meta: meta });
      toast.success("Description updated");
      setRewrite(null); setRewriteResult(""); setCompareResults(null); setCompareChoice(null);
      load(); loadAnalytics();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Update failed"); }
    finally { setApplying(false); }
  };

  const runAutoFix = async (jobId, title) => {
    setAutoFix({ jobId, title });
    setAutoFixResult(null);
    setAutoFixBusy(true);
    try {
      const { data } = await api.post("/ai/rewrite-jd-auto-fix", { job_id: jobId, seniority: "Senior" });
      setAutoFixResult(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Auto-fix failed"); setAutoFix(null); }
    finally { setAutoFixBusy(false); }
  };

  const applyAutoFix = async () => {
    if (!autoFix || !autoFixResult) return;
    setAutoFixApplying(true);
    try {
      await api.patch(`/recruit/jobs/${autoFix.jobId}`, {
        description: autoFixResult.description,
        _rewrite_meta: {
          source: "auto-fix",
          mode: autoFixResult.chosen_mode,
          rationale: autoFixResult.rationale,
          seniority: autoFixResult.chosen_mode === "seniority" ? autoFixResult.seniority_level : null,
        },
      });
      toast.success(`Description updated · applied "${autoFixResult.chosen_mode}"`);
      setAutoFix(null); setAutoFixResult(null);
      load(); loadAnalytics();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Apply failed"); }
    finally { setAutoFixApplying(false); }
  };

  return (
    <div className="space-y-6" data-testid="jd-library-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
          <h1 className="text-3xl font-bold font-display mt-1">Job descriptions</h1>
          <p className="text-neutral-600 mt-1 text-sm">
            {items.length} descriptions in your library · reuse, rewrite, or clone into new jobs.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, dept, keywords…" className="pl-9" data-testid="jd-search" />
        </div>
      </div>

      {flagged.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-500 bg-red-50/40" data-testid="jd-weakness-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h3 className="font-semibold text-sm">
                {flagged.length} description{flagged.length > 1 ? "s" : ""} need{flagged.length === 1 ? "s" : ""} attention
              </h3>
              <Badge className="text-[10px] bg-red-500 text-white border-0">Rewrite recommended</Badge>
            </div>
            <div className="space-y-2">
              {flagged.slice(0, 4).map(a => (
                <div key={a.job_id} className="flex items-center justify-between gap-3 flex-wrap p-3 bg-white rounded-lg border" data-testid={`jd-alert-${a.job_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{a.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">{a.alert.reason}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Badge className={`text-[10px] ${a.alert.level === "critical" ? "bg-red-500" : "bg-amber-500"} text-white border-0`}>
                      {a.alert.level}
                    </Badge>
                    <Button size="sm" onClick={() => runAutoFix(a.job_id, a.title)} data-testid={`jd-autofix-${a.job_id}`} className="btn-primary rounded-full">
                      <Zap className="w-3.5 h-3.5 mr-1" /> Auto-fix
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      const job = items.find(x => x.job_id === a.job_id);
                      if (job) { setOpenId(job.job_id); openRewrite(job); }
                    }} data-testid={`jd-fix-${a.job_id}`}>
                      <Wand2 className="w-3.5 h-3.5 mr-1 text-pink" /> Manual
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {topPerformers.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-pink/5 to-teal/5" data-testid="jd-analytics-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-pink" />
              <h3 className="font-semibold text-sm">Descriptions attracting the strongest candidates</h3>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {topPerformers.map((a, idx) => (
                <Link key={a.job_id} to={`/app/recruit/jobs/${a.job_id}`} data-testid={`jd-top-${a.job_id}`}
                  className="rounded-xl bg-white p-3 border hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">#{idx + 1} performer</div>
                    <Badge className="text-[10px] bg-teal text-white border-0">{a.avg_ai_score} avg</Badge>
                  </div>
                  <div className="font-semibold text-sm truncate">{a.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{a.department} · {a.candidates} candidates</div>
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <span className="text-neutral-700 font-medium">{a.strong_pct != null ? `${a.strong_pct}%` : "—"}</span>
                    <span className="text-neutral-500">Strong</span>
                    <span className="text-neutral-300">·</span>
                    <span className="text-neutral-700 font-medium">{a.hired}</span>
                    <span className="text-neutral-500">hired</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-16 text-center text-neutral-500">
            <FileText className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
            {items.length === 0 ? "No job descriptions yet. Create one from the Jobs page." : "No descriptions match your search."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(j => {
            const open = openId === j.job_id;
            const stats = analyticsById[j.job_id];
            return (
              <Card key={j.job_id} className="border-0 shadow-sm" data-testid={`jd-card-${j.job_id}`}>
                <CardContent className="p-0">
                  <button
                    onClick={() => setOpenId(open ? null : j.job_id)}
                    className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-neutral-50 transition-colors"
                    data-testid={`jd-toggle-${j.job_id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: "#FFE0F0" }}>
                        <FileText className="w-5 h-5 text-pink" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg truncate">{j.title}</h3>
                          <Badge variant="outline" className="text-[10px]">{j.status}</Badge>
                          {stats?.avg_ai_score != null && (
                            <Badge className="text-[10px] bg-teal text-white border-0" title="Avg AI score of candidates from this JD">
                              <BarChart3 className="w-3 h-3 mr-1" /> {stats.avg_ai_score} avg
                            </Badge>
                          )}
                          {stats?.alert && (
                            <Badge className={`text-[10px] ${stats.alert.level === "critical" ? "bg-red-500" : "bg-amber-500"} text-white border-0`} title={stats.alert.reason} data-testid={`jd-alert-badge-${j.job_id}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" /> Needs rewrite
                            </Badge>
                          )}
                          {stats?.guardrail && (
                            <Badge className={`text-[10px] ${stats.guardrail.severity === "critical" ? "bg-red-500" : "bg-amber-500"} text-white border-0`}
                              title={`Rewrite applied ${stats.guardrail.days_since}d ago — avg dropped ${Math.abs(stats.guardrail.delta)} pts. Open History to revert.`}
                              data-testid={`jd-guardrail-badge-${j.job_id}`}>
                              <History className="w-3 h-3 mr-1" /> Rewrite hurting
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                          <span>{j.department}</span><span>·</span>
                          <span>{j.location}</span><span>·</span>
                          <span>{j.word_count} words</span>
                          {stats && (<><span>·</span><span>{stats.candidates} candidates</span></>)}
                          {stats && stats.strong_pct != null && (<><span>·</span><span>{stats.strong_pct}% strong</span></>)}
                        </div>
                        {!open && j.description && (
                          <p className="text-sm text-neutral-600 mt-2 line-clamp-2">
                            {j.description.replace(/[#*`_>-]/g, "").slice(0, 260)}
                          </p>
                        )}
                      </div>
                    </div>
                    {open ? <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0 mt-2" /> : <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0 mt-2" />}
                  </button>

                  {open && (
                    <div className="border-t px-5 py-5 bg-neutral-50/40" data-testid={`jd-expanded-${j.job_id}`}>
                      {j.requirements?.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-1.5">
                          {j.requirements.map(r => <Badge key={r} variant="outline" className="text-xs">{r}</Badge>)}
                        </div>
                      )}
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-neutral-800 leading-relaxed" data-testid={`jd-full-${j.job_id}`}>
                        {j.description || <span className="text-neutral-400 italic">No description written.</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t">
                        <Button variant="outline" size="sm" onClick={() => copyText(j.description)} data-testid={`jd-copy-${j.job_id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy text
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openRewrite(j)} data-testid={`jd-rewrite-${j.job_id}`}>
                          <Wand2 className="w-3.5 h-3.5 mr-1.5 text-pink" /> Rewrite with Alfred
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setReuse(j); setReuseTitle(j.title + " (copy)"); }} data-testid={`jd-reuse-${j.job_id}`}>
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Reuse in new job
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setHistoryJobId(j.job_id)} data-testid={`jd-history-${j.job_id}`}>
                          <History className="w-3.5 h-3.5 mr-1.5" /> History
                        </Button>
                        <Link to={`/app/recruit/jobs/${j.job_id}`}>
                          <Button variant="ghost" size="sm" data-testid={`jd-open-${j.job_id}`}>
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open job
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!reuse} onOpenChange={(v) => { if (!v) { setReuse(null); setReuseTitle(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reuse description in new job</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">Clone this JD into a fresh job with a new title.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="text-sm text-neutral-600">Cloning description, requirements, department & location from <span className="font-semibold">{reuse?.title}</span>.</div>
            <div>
              <label className="text-xs font-medium text-neutral-500">New job title</label>
              <Input value={reuseTitle} onChange={(e) => setReuseTitle(e.target.value)} className="mt-1" data-testid="jd-reuse-title" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500">Description preview</label>
              <Textarea value={reuse?.description || ""} readOnly rows={5} className="mt-1 text-xs bg-neutral-50" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReuse(null); setReuseTitle(""); }}>Cancel</Button>
            <Button className="btn-primary" onClick={reuseAsNewJob} disabled={saving || !reuseTitle} data-testid="jd-reuse-confirm">
              {saving ? "Creating…" : "Create job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rewrite} onOpenChange={(v) => { if (!v) { setRewrite(null); setRewriteResult(""); setCompareResults(null); setCompareChoice(null); } }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="jd-rewrite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-pink" /> Rewrite with Alfred
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Alfred rewrites the description using Claude Sonnet 5. Preview the result before applying it to the job.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="text-xs text-neutral-500">Job: <span className="font-semibold text-neutral-900">{rewrite?.job?.title}</span></div>

            {!compareResults && !compareBusy && (
              <>
                <div className="flex flex-wrap gap-2" data-testid="rewrite-mode-chips">
                  {MODES.map(m => (
                    <button key={m.k} onClick={() => setRewriteMode(m.k)} data-testid={`rewrite-mode-${m.k}`}
                      className={`text-left px-3 py-2 rounded-lg border text-xs transition-all min-w-[150px] ${rewriteMode === m.k ? "border-pink bg-pink/5 text-pink font-semibold" : "hover:bg-neutral-50"}`}>
                      <div className="font-semibold">{m.label}</div>
                      <div className="text-[10px] text-neutral-500 font-normal mt-0.5">{m.desc}</div>
                    </button>
                  ))}
                  <button onClick={runCompare} data-testid="compare-all-btn"
                    className="text-left px-3 py-2 rounded-lg border text-xs transition-all min-w-[150px] hover:bg-neutral-50 border-dashed">
                    <div className="font-semibold flex items-center gap-1.5"><Columns3 className="w-3 h-3" /> Compare all 3</div>
                    <div className="text-[10px] text-neutral-500 font-normal mt-0.5">See every mode side-by-side.</div>
                  </button>
                </div>

                {rewriteMode === "seniority" && (
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-neutral-500">Target level:</span>
                    {["Junior", "Mid", "Senior", "Staff", "Principal"].map(l => (
                      <button key={l} onClick={() => setRewriteSeniority(l)} data-testid={`seniority-${l.toLowerCase()}`}
                        className={`px-2.5 py-1 rounded-full text-xs border ${rewriteSeniority === l ? "bg-pink text-white border-pink" : "hover:bg-neutral-50"}`}>{l}</button>
                    ))}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-medium text-neutral-500 mb-1">Original</div>
                    <Textarea value={rewrite?.job?.description || ""} readOnly rows={16} className="text-xs bg-neutral-50" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-neutral-500 mb-1">Alfred's rewrite</div>
                    <Textarea value={rewriteResult} onChange={(e) => setRewriteResult(e.target.value)} rows={16}
                      placeholder={rewriteBusy ? "Alfred is rewriting…" : "Click 'Generate rewrite' to preview."}
                      className="text-xs" data-testid="rewrite-result-textarea" />
                  </div>
                </div>
              </>
            )}

            {compareBusy && (
              <div className="py-16 text-center text-neutral-500 text-sm" data-testid="compare-loading">
                <Sparkles className="w-6 h-6 mx-auto mb-3 text-pink animate-pulse" />
                Alfred is drafting all three rewrites in parallel — this takes 10-15 seconds…
              </div>
            )}

            {compareResults && (
              <div data-testid="compare-results">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="font-semibold">Pick your favourite</span>
                    <span className="text-neutral-500"> — click a column to select, then Apply.</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-neutral-500 text-xs">Seniority level:</span>
                    {["Junior", "Mid", "Senior", "Staff", "Principal"].map(l => (
                      <button key={l} onClick={() => setRewriteSeniority(l)}
                        className={`px-2 py-0.5 rounded-full text-[10px] border ${rewriteSeniority === l ? "bg-pink text-white border-pink" : "hover:bg-neutral-50"}`}>{l}</button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={runCompare} disabled={compareBusy} className="text-xs" data-testid="compare-regen-btn">Regenerate</Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {["tone", "inclusivity", "seniority"].map(k => {
                    const meta = COMPARE_LABELS[k];
                    const selected = compareChoice === k;
                    return (
                      <button key={k} onClick={() => setCompareChoice(k)} data-testid={`compare-col-${k}`}
                        className={`text-left p-3 rounded-xl border-2 transition-all bg-white ${selected ? "border-pink shadow-md" : "border-neutral-200 hover:border-neutral-300"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${meta.color}`} />
                            <span className="font-semibold text-xs">{meta.label}</span>
                          </div>
                          {selected && <Badge className="text-[9px] bg-pink text-white border-0">Selected</Badge>}
                        </div>
                        <div className="text-[11px] text-neutral-700 whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto pr-1" data-testid={`compare-text-${k}`}>
                          {compareResults[k]?.slice(0, 1400) || "—"}
                          {compareResults[k]?.length > 1400 && "…"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="justify-between sm:justify-between">
            {!compareResults ? (
              <Button variant="ghost" onClick={runRewrite} disabled={rewriteBusy} data-testid="rewrite-generate-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5 text-pink" /> {rewriteBusy ? "Rewriting…" : rewriteResult ? "Regenerate" : "Generate rewrite"}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => { setCompareResults(null); setCompareChoice(null); }} data-testid="compare-back-btn">
                ← Back to single mode
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setRewrite(null); setRewriteResult(""); setCompareResults(null); setCompareChoice(null); }}>Cancel</Button>
              <Button className="btn-primary" onClick={applyRewrite}
                disabled={applying || (!compareResults && !rewriteResult) || (compareResults && !compareChoice)}
                data-testid="rewrite-apply-btn">
                {applying ? "Applying…" : compareResults ? (compareChoice ? `Apply ${COMPARE_LABELS[compareChoice].label}` : "Pick one to apply") : "Apply to job"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!autoFix} onOpenChange={(v) => { if (!v) { setAutoFix(null); setAutoFixResult(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="jd-autofix-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-pink" /> Auto-fix with Alfred
            </DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Alfred generates all 3 rewrites for <span className="font-semibold">{autoFix?.title}</span> and picks the strongest — you review the choice and apply in one click.
            </DialogDescription>
          </DialogHeader>
          {autoFixBusy && (
            <div className="py-16 text-center text-neutral-500 text-sm" data-testid="autofix-loading">
              <Sparkles className="w-6 h-6 mx-auto mb-3 text-pink animate-pulse" />
              Alfred is drafting rewrites and choosing the best — this takes 15-20 seconds…
            </div>
          )}
          {autoFixResult && (
            <div className="space-y-4" data-testid="autofix-result">
              <div className="rounded-lg border-2 border-pink bg-pink/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="text-[10px] bg-pink text-white border-0">Alfred picked</Badge>
                  <span className="font-semibold text-sm capitalize" data-testid="autofix-chosen-mode">
                    {autoFixResult.chosen_mode === "seniority" ? `Adjust seniority (${autoFixResult.seniority_level})` : autoFixResult.chosen_mode + " rewrite"}
                  </span>
                </div>
                <p className="text-xs text-neutral-700" data-testid="autofix-rationale">{autoFixResult.rationale}</p>
              </div>
              <div>
                <div className="text-xs font-medium text-neutral-500 mb-1">Preview</div>
                <Textarea value={autoFixResult.description} onChange={(e) => setAutoFixResult({ ...autoFixResult, description: e.target.value })}
                  rows={14} className="text-xs" data-testid="autofix-preview" />
              </div>
            </div>
          )}
          <DialogFooter className="justify-between sm:justify-between">
            <Button variant="ghost" onClick={() => runAutoFix(autoFix?.jobId, autoFix?.title)} disabled={autoFixBusy || !autoFix} data-testid="autofix-regen-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-pink" /> Regenerate
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setAutoFix(null); setAutoFixResult(null); }}>Cancel</Button>
              <Button className="btn-primary" onClick={applyAutoFix} disabled={!autoFixResult || autoFixApplying} data-testid="autofix-apply-btn">
                {autoFixApplying ? "Applying…" : "Apply to job"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RewriteHistory
        jobId={historyJobId}
        open={!!historyJobId}
        onOpenChange={(v) => { if (!v) setHistoryJobId(null); }}
        onReverted={() => { load(); loadAnalytics(); }}
      />
    </div>
  );
}
